import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Main } from '@/components/layout/main'

import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { list_cabinet_templates, list_template_categories, add_template_favorite, remove_template_favorite, delete_cabinet_template } from '@/api/document-templates-cabinet'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DataTable } from '@/components/table'
import { ColumnDef } from '@tanstack/react-table'
import type { DocumentTemplate } from '@/types/document-template'
import DataTableSkeleton from '@/components/ui/data-table-skeleton'
import { FileText, Star, Plus } from 'lucide-react'
import { getLocalizedCategoryName } from '@/lib/localization-helpers'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Link } from '@tanstack/react-router'
import { CreateTemplateRequestDialog } from '@/views/template-requests/create-dialog'
import { PermissionGuard } from '@/components/permission-guard'
import { UserPermission } from '@/constants/permissions'
import { useToast } from '@/hooks/use-toast'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuShortcut, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { DotsHorizontalIcon } from '@radix-ui/react-icons'
import { IconEdit, IconTrash } from '@tabler/icons-react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

export const Route = createFileRoute('/_authenticated/cabinet-document-templates')({
  component: Page,
})

interface CabinetTemplatesParams {
  page: number
  page_size: number
  q: string | null
  category_id: string | null
  favorites_only: boolean | null
}

function Page() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [items, setItems] = useState<DocumentTemplate[]>([])
  const [count, setCount] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<boolean>(false)
  const [categories, setCategories] = useState<Array<{ id: string; name: string; name_ar?: string }>>([])
  const [search, setSearch] = useState<string>('')
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [templateToDelete, setTemplateToDelete] = useState<DocumentTemplate | null>(null)

  const [params, setParams] = useState<CabinetTemplatesParams>({
    page: 0,
    page_size: 10,
    q: null,
    category_id: null,
    favorites_only: null,
  })

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await list_template_categories()
        setCategories(cats)
      } catch (_e) {
        setCategories([])
      }
    }
    loadCategories()
  }, [])

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setParams(prev => ({ ...prev, q: search || null, page: 0 }))
    }, 500)

    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    const load = async () => {
      setLoading(true)
      setError(false)
      try {
        const data = await list_cabinet_templates({
          q: params.q,
          category_id: params.category_id,
          limit: params.page_size,
          offset: params.page * params.page_size,
          favorites_only: params.favorites_only,
        })
        setItems(data?.results ?? [])
        setCount(data?.count ?? 0)
      } catch (_) {
        setError(true)
        setItems([])
        setCount(0)
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [params])

  const handleDelete = async () => {
    if (!templateToDelete) return
    try {
      await delete_cabinet_template(templateToDelete.slug)
      toast({ title: t('success'), description: t('template_deleted_successfully') || 'Template deleted successfully' })
      setDeleteDialogOpen(false)
      setTemplateToDelete(null)
      setParams(prev => ({ ...prev }))
    } catch (error: any) {
      toast({
        title: t('error'),
        description: error?.detail || error?.message || t('error_deleting_template') || 'Failed to delete template',
        variant: 'destructive',
      })
    }
  }

  const columns: ColumnDef<DocumentTemplate>[] = [
    {
      id: 'favorite',
      header: t('favorite') || 'Favorite',
      cell: ({ row }) => {
        const isFav = !!row.original.is_favorite
        const toggleFavorite = async (e: React.MouseEvent) => {
          e.stopPropagation()
          const templateId = row.original.id
          const prev = isFav
          setItems((prevItems) => prevItems.map((it) => it.id === templateId ? { ...it, is_favorite: !prev } : it))
          try {
            if (!prev) {
              await add_template_favorite(templateId)
            } else {
              await remove_template_favorite(templateId)
            }
          } catch (_e) {
            setItems((prevItems) => prevItems.map((it) => it.id === templateId ? { ...it, is_favorite: prev } : it))
          }
        }
        return (
          <Button variant='ghost' size='icon' onClick={toggleFavorite} aria-label={isFav ? (t('unfavorite') || 'Unfavorite') : (t('mark_favorite') || 'Mark favorite')}>
            <Star className={isFav ? 'h-4 w-4 text-yellow-500' : 'h-4 w-4 text-muted-foreground'} fill={isFav ? 'currentColor' : 'none'} />
          </Button>
        )
      },
    },
    {
      accessorKey: 'title',
      header: t('title'),
      cell: ({ row }) => (
        <div className='flex flex-col'>
          <p className='font-medium'>{row.original.title}</p>
          <p className='text-xs text-muted-foreground'>{row.original.slug}</p>
        </div>
      ),
    },
    {
      accessorKey: 'version',
      header: t('version'),
      cell: ({ row }) => <span className='text-sm'>v{row.original.version}</span>,
    },
    {
      accessorKey: 'category',
      header: t('category'),
      cell: ({ row }) => {
        const category = row.original.category
        if (!category) return <span className='text-sm text-muted-foreground'>-</span>
        if (typeof category === 'string') return <span className='text-sm'>{category}</span>
        return <span className='text-sm'>{getLocalizedCategoryName(category, i18n.language)}</span>
      },
    },
    {
      accessorKey: 'engine',
      header: t('engine') || 'Engine',
      cell: ({ row }) => (
        <Badge variant={row.original.engine === 'word' ? 'default' : 'secondary'}>
          {row.original.engine?.toUpperCase() || 'HTML'}
        </Badge>
      ),
    },
    {
      accessorKey: 'is_active',
      header: t('status'),
      cell: ({ row }) => (
        <Badge variant={row.original.is_active ? 'default' : 'secondary'}>
          {row.original.is_active ? t('active') : t('inactive')}
        </Badge>
      ),
    },
    {
      id: 'actions',
      cell: ({ row }) => (
        <PermissionGuard permission={UserPermission.CHANGE_DOCUMENT_TEMPLATE}>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                className='flex h-8 w-8 p-0 data-[state=open]:bg-muted'
                onClick={(e) => e.stopPropagation()}
              >
                <DotsHorizontalIcon className='h-4 w-4' />
                <span className='sr-only'>{t('open_menu')}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='w-[160px]'>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  navigate({ to: `/cabinet-document-template/${row.original.slug}` })
                }}
              >
                {t('edit')}
                <DropdownMenuShortcut><IconEdit size={16} /></DropdownMenuShortcut>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <PermissionGuard permission={UserPermission.DELETE_DOCUMENT_TEMPLATE}>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation()
                    setTemplateToDelete(row.original)
                    setDeleteDialogOpen(true)
                  }}
                  className='!text-red-500'
                >
                  {t('delete')}
                  <DropdownMenuShortcut><IconTrash size={16} /></DropdownMenuShortcut>
                </DropdownMenuItem>
              </PermissionGuard>
            </DropdownMenuContent>
          </DropdownMenu>
        </PermissionGuard>
      ),
    },
  ]

  const handleRowClick = (row: DocumentTemplate) => {
    navigate({ to: `/cabinet-document-template/${row.slug}` })
  }

  return (
    <>


      <Main>
        <div className='mb-6 flex items-center justify-between'>
          <div>
            <h2 className='text-2xl font-bold tracking-tight flex items-center gap-2'>
              <FileText className='w-6 h-6' />
              {t('document_templates') || 'Document Templates'}
            </h2>
            <p className='text-muted-foreground'>
              {t('document_templates_desc') || 'Browse and generate documents from available templates'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button asChild variant="outline">
              <Link to="/template-requests">
                {t('my_requests')}
              </Link>
            </Button>
            <PermissionGuard permission={UserPermission.ADD_TEMPLATE_REQUEST}>
              <CreateTemplateRequestDialog
                trigger={<Button variant="outline">{t('request_template')}</Button>}
              />
            </PermissionGuard>
            <PermissionGuard permission={UserPermission.ADD_DOCUMENT_TEMPLATE}>
              <Button
                className='gap-2'
                onClick={() => navigate({ to: '/cabinet-document-template-create' })}
              >
                <Plus className='w-4 h-4' />
                {t('create_template') || 'Create Template'}
              </Button>
            </PermissionGuard>
          </div>
        </div>

        <div className='mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4'>
          <div className='flex-1'>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('search_templates') || 'Search templates...'}
            />
          </div>
          <div className='w-full sm:w-48'>
            <Select
              value={params.category_id || 'all'}
              onValueChange={(value) => {
                setParams({ ...params, category_id: value === 'all' ? null : value, page: 0 })
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('all_categories') || 'All Categories'} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>{t('all_categories') || 'All Categories'}</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {getLocalizedCategoryName(cat, i18n.language)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div
            className={`flex items-center gap-2 mt-1 ${i18n.language === 'ar' ? 'flex-row-reverse' : ''} mb-3`}
            aria-live='polite'
          >
            <Checkbox
              id='favorites-only'
              checked={!!params.favorites_only}
              onCheckedChange={(checked) => setParams({ ...params, favorites_only: checked ? true : null, page: 0 })}
            />
            <label
              htmlFor='favorites-only'
              className='text-sm text-muted-foreground'
              dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
            >
              {t('favorites_only') || 'Favorites only'}
            </label>
          </div>
        </div>

        {loading ? (
          <DataTableSkeleton columns={5} rows={5} />
        ) : error ? (
          <div className='py-8 text-center text-destructive'>
            {t('error_loading') || 'Error loading templates'}
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={items}
            pages={Math.ceil(count / params.page_size)}
            params={params}
            setParams={setParams}
            showPagination={true}
            onRowClick={handleRowClick}
          />
        )}
      </Main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('confirm_delete') || 'Confirm Delete'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('delete_template_confirmation') || 'Are you sure you want to delete this template?'}{' '}
              {templateToDelete && (
                <span className="font-semibold">"{templateToDelete.title}"</span>
              )}
              {'. '}
              {t('action_cannot_be_undone') || 'This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}